import React, { useState } from 'react'
import { graphql } from 'gatsby'
import { navigate } from 'gatsby'
import styled from 'styled-components'
import Helmet from 'react-helmet'
import favicon from '../images/favicon.ico'
import { Arrow } from '../assets/svg/criticalSVG'
import BlogCategoryList from '../components/BlogCategoryList'
import blogListings from '../../content/blogListings.json'
import BlogPostCard from '../components/BlogPostCard'
import Breadcrumbs from '../components/Breadcrumbs'
import Button from '../components/Button'
import Container from '../Layout/Container'
import CTA from '../sections/CTA'
import Layout from '../Layout/Layout'
import ReactPaginate from 'react-paginate'
import SEO from '../components/seo'
import TextHeader from '../components/TextHeader'



const StyledBlog = styled.div`
  padding: 203px 24px 0 24px;

  ${({ theme }) => theme.media.desktop} {
    padding-left: 0;
    padding-right: 0;
  }
`

const StyledHeader = styled.h1`
  font-size: 24px;
  font-weight: 400;
  line-height: 1.5;
  margin-bottom: 32px;
`

const StyledRow = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 64px;
  padding-bottom: 64px;
  width: 100%;

  ${({ theme }) => theme.media.desktop} {
    flex-direction: row;
    margin-bottom: 64px;
  }

  &:last-child {
    margin-bottom: 0;
  }
`

const StyledColumn = styled.div`
  display: block;
  margin-bottom: 24px;
  width: 100%;

  ${({ theme }) => theme.media.desktop} {
    margin-bottom: 0;
    width: ${({ width }) => width}%;
  }
`

const BlogGrid = styled.div`
  display: grid;
  grid-gap: 24px;
  grid-template-columns: 1fr;
  width: 100%;

  ${({ theme }) => theme.media.desktop} {
    grid-template-columns: repeat(3, 1fr);
  }
`

const StyledPagination = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  margin-top: 8px;
  padding-bottom: 88px;

  ul {
    display: flex;

    li {
      &.previous {
        transform: rotate(90deg);
      }

      &.next {
        transform: rotate(-90deg);
      }

      a {
        color: #8a94a6;
        font-size: 15px;
        line-height: 1.67;
        padding: 0 4px;

        &[aria-current] {
          color: #4e5d78;
          font-weight: 500;
        }
      }
    }
  }

  ${({ theme }) => theme.media.desktop} {
    flex-direction: row;
    justify-content: space-between;
  }
`

const StyledPaginationInfo = styled.p`
  color: #8a94a6;
  font-size: 15px;
  line-height: 1.67;
`

const Pagination = ({ pageContext, totalItems }) => {
  let paginationItems = []

  for (let i = 0; i < pageContext.numberOfPages; i++) {
    paginationItems.push(i + 1)
  }

  if (totalItems <= 24) {
    pageContext.numberOfPages = 2
  }

  const { limit, pageNumber } = pageContext
  // Filter/Reduce to a dict as there may be duplicates
  const allPosts = Object.values(
    blogListings.reduce((acc, item) => {
      if (item.category.slug === pageContext.slug) {
        acc[item.id] = item
      }
      return acc
    }, {})
  )

  const pagePosts = allPosts.slice(pageNumber * limit, (pageNumber + 1) * limit)

  return (
    <StyledPagination>
      <ReactPaginate
        pageCount={pageContext.numberOfPages}
        pageRangeDisplayed={6}
        marginPagesDisplayed={2}
        initialPage={pageContext.pageNumber}
        hrefBuilder={page =>
          page === 1
            ? `/blog/category/${pageContext.slug}`
            : `/blog/category/${pageContext.slug}/page/${page}`
        }
        onPageChange={page =>
          page.selected === 0
            ? navigate(`/blog/category/${pageContext.slug}`)
            : navigate(
                `/blog/category/${pageContext.slug}/page/${page.selected + 1}`
              )
        }
        disableInitialCallback={true}
        previousLabel={<Arrow />}
        nextLabel={<Arrow />}
      />
      <StyledPaginationInfo>
        {pageContext.skip + 1} - {pageContext.skip + pagePosts.length} out of{' '}
        {totalItems} items
      </StyledPaginationInfo>
    </StyledPagination>
  )
}

const BlogCategory = ({ data, pageContext }) => {
  const [searchMode] = useState(false)
  const [searchResults] = useState([])

  const { limit, pageNumber } = pageContext
  // Filter/Reduce to a dict as there may be duplicates
  const allPosts = Object.values(
    blogListings.reduce((acc, item) => {
      if (item.category.slug === pageContext.slug) {
        acc[item.id] = item
      }
      return acc
    }, {})
  )
  const posts = allPosts.slice(pageNumber * limit, (pageNumber + 1) * limit)
  const categories = data.allContentfulBlogCategory.edges

  return (
    <>
      <SEO
        title={pageContext.name}
        url={`https://www.mailgun.com/blog/category/${pageContext.slug}/`}
        type="article"
      />
      <Helmet defer={false}>
        <title>{pageContext.name}</title>
        {pageContext.pageNumber === 0 ? (
          <link rel="canonical" href={`https://www.mailgun.com/blog/category/${pageContext.slug}/`}/>
        ) : (
          <link rel="canonical" href={`https://www.mailgun.com/blog/category/${pageContext.slug}/page/${pageNumber + 1}/`} />
        )}
        <link rel="icon" href={favicon} />
      </Helmet>
      <Layout singlePost={true} blog="true">
        <Container greyBG fullWidth>
          <Container>
            <StyledBlog>
              <Breadcrumbs
                items={[
                  { name: 'Blog', path: '/blog' },
                  {
                    name: pageContext.name,
                    path: `/blog/category/${pageContext.slug}`,
                  },
                ]}
              />
              <BlogCategoryList current={pageContext.slug} data={categories} />
              <StyledRow>
                <StyledColumn width="100">
                  <StyledHeader>
                    {!searchMode
                      ? pageContext.name
                      : `Searching for "${searchResults.query}"`}
                  </StyledHeader>
                  <BlogGrid>
                    {!searchMode &&
                      posts.map(post => (
                        <BlogPostCard key={post.id} data={post} />
                      ))}
                    {searchMode ? (
                      searchResults.results.length > 0 ? (
                        searchResults.results.map(post => (
                          <BlogPostCard key={post.id} data={post} />
                        ))
                      ) : (
                        <div>No results found</div>
                      )
                    ) : null}
                  </BlogGrid>
                </StyledColumn>
              </StyledRow>
            </StyledBlog>
            {!searchMode && (
              <Pagination
                pageContext={pageContext}
                totalItems={allPosts.length}
              />
            )}
          </Container>
        </Container>
        <Container fullWidth>
          <CTA ghost slick>
            <TextHeader
              title={
                <div className="title">
                  It's easy to get started. <span>And it's free.</span>
                </div>
              }
              subtitle="sign up"
              text="See what you can accomplish with the world's best email delivery platform."
              theme={{
                size: 'big',
                align: 'center',
              }}
            />
            <Button
              primary="true"
              xxl="true"
              to="https://signup.mailgun.com/"
              external
              isAbsolutePath
            >
              Sign up for Free
            </Button>
          </CTA>
        </Container>
      </Layout>
    </>
  )
}

export const query = graphql`
  query {
    allContentfulBlogCategory {
      edges {
        node {
          id
          name
          slug
          color
          colorOpacity
          icon {
            file {
              url
            }
          }
        }
      }
    }
  }
`
export default BlogCategory
